import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  FieldError,
  Fieldset,
  FlexRow,
  Form,
  IconGlobeAlt,
  IconInstagram,
  IconTwitter,
  Input,
  InputPre,
  Label,
  RequiredText,
  RichTextEditor,
  UnexpectedError,
} from "atoms";
import { ImageUpload } from "features/images";
import { AffiliatedReviewableInput } from "features/search";
import { fieldOptions, validators } from "utils/form";
import { useCollectiveForm } from "../api/useCollectiveForm";
import type { Collective } from "../types";

type CollectiveFormProps = {
  collective?: Collective;
};

const linkInputs = [
  [
    "Personal Website",
    "website",
    "https://your-website.com",
    <IconGlobeAlt aria-hidden />,
  ],
  [
    "Instagram URL",
    "instagram",
    "https://instagram.com/your-username",
    <IconInstagram aria-hidden />,
  ],
  [
    "Twitter URL",
    "twitter",
    "https://twitter.com/your-username",
    <IconTwitter aria-hidden />,
  ],
] as const;

function CollectiveForm({ collective }: CollectiveFormProps) {
  const navigate = useNavigate();

  const {
    control,
    formState: { errors },
    isEdit,
    mutation,
    onSubmit,
    register,
    setValue,
  } = useCollectiveForm({
    collective,
    onSuccess: (newCollective) =>
      navigate(`/admin/collectives/${newCollective.id}`),
  });

  return (
    <Form onSubmit={onSubmit}>
      <Label>
        Collective Slug
        <RequiredText />
        <Input
          {...register("id", {
            ...fieldOptions.required,
            validate: validators.validSlug,
          })}
          disabled={isEdit}
        />
        {errors.id && <FieldError>{errors.id.message}</FieldError>}
      </Label>

      <Label>
        Name
        <RequiredText />
        <Input
          {...register("name", {
            ...fieldOptions.required,
            ...fieldOptions.maxLength(80),
          })}
        />
        {errors.name && <FieldError>{errors.name.message}</FieldError>}
      </Label>

      <Checkbox
        {...register("hasCollectivePage")}
        label={<strong>Has collective page</strong>}
      />

      <Label>
        Description
        <RequiredText />
        <RichTextEditor
          defaultValue={collective?.description}
          onChange={(html) => setValue("description", html)}
        />
        {errors.description && (
          <FieldError>{errors.description.message}</FieldError>
        )}
      </Label>

      <ImageUpload
        register={register}
        setValue={setValue}
        fieldName="badge"
        src={collective?.badge}
        aspect="square"
        label={
          <>
            Badge
            <RequiredText />
          </>
        }
        errorMessage={errors.badge?.url?.message}
        required
      />

      <ImageUpload
        register={register}
        setValue={setValue}
        fieldName="banner"
        src={collective?.banner}
        aspect="banner"
        label="Banner"
        errorMessage={errors.banner?.url?.message}
      />

      {linkInputs.map(([label, type, placeholder, icon], index) => (
        <Label key={`${type}-${label}`}>
          {label}
          <FlexRow gap="0">
            <InputPre>{icon}</InputPre>
            <Input
              {...register(`links.${index}.url`, {
                validate: (val: string) => !val || validators.validURL(val),
              })}
              placeholder={placeholder}
              autoCapitalize="off"
            />
          </FlexRow>
          <Input
            type="hidden"
            {...register(`links.${index}.type`)}
            value={type}
          />
          {errors.links?.[index]?.url && (
            <FieldError>{errors.links?.[index]?.url?.message}</FieldError>
          )}
        </Label>
      ))}

      <Fieldset>
        Add {isEdit && "and Remove"} Artists
        <AffiliatedReviewableInput
          control={control}
          name="affiliatedArtists"
          typeLabel="Artists"
        />
      </Fieldset>

      {mutation.error && <UnexpectedError error={mutation.error} />}
      <div>
        <br />
        <FlexRow>
          <Button disabled={mutation.isLoading} type="submit">
            {isEdit ? "Update" : "Create"} Collective
          </Button>
        </FlexRow>
      </div>
    </Form>
  );
}

export { CollectiveForm };
